import { isNonEmptyArray } from "web/services/helpers"
import { Button, Icon, ImportErrors, Loading, Table } from "web/components"
import { formatAsRelativeDateTime } from "web/services/format"
import { useImportSheet } from "web/hooks/data"
import { useEffect, useState } from "react"
import { pluralize } from "web/services/inflector"

function ConfirmImport({ selectedSheet }) {
  async function handleConfirmation() {
    await selectedSheet.confirm(selectedSheet.id)
  }

  return (
    <>
      <p className="mt-4 text-sm text-neutral-5">
        Validation passed, ready to import data.
      </p>
      <Button
        className="mt-3"
        disableAfterClick={true}
        onClick={handleConfirmation}
      >
        Confirm
      </Button>
    </>
  )
}

function RevalidateImport({ selectedSheet }) {
  async function handleReprocess() {
    await selectedSheet.reprocess(selectedSheet.id)
  }

  return (
    <>
      <p className="mt-4 text-sm text-neutral-5">
        Validation failed, fix errors and revalidate.
      </p>
      <div className="mt-4">
        <Button disableAfterClick={true} onClick={handleReprocess}>
          Revalidate
        </Button>
      </div>
    </>
  )
}

export default function TableData({ route, importObj, deprecatedTypes = [] }) {
  const [doingWork, setDoingWork] = useState(false)
  const selectedSheet =
    importObj.sheets.length > 1
      ? importObj.sheets.find((sheet) => sheet.id === route[0].key)
      : importObj.sheets[0]

  const sheet = useImportSheet({
    importId: importObj.id,
    sheetId: selectedSheet?.id,
    parentKeys: [importObj.swrKey],
    swrOptions: {
      refreshInterval: doingWork ? 5000 : 0,
    },
  })

  const errors = sheet.errors
  const hasImportTableData = isNonEmptyArray(sheet.imported_rows)
  const importTableData = sheet?.imported_rows
  const canConfirm =
    sheet.status === "processed" && !deprecatedTypes.includes(importObj.type)
  const canRevalidate =
    sheet.status === "failed_on_processing" &&
    !deprecatedTypes.includes(importObj.type)

  const sheetDoingWork = [
    "queued_for_processing",
    "processing",
    "queued_for_applying",
    "applying",
  ].includes(sheet.status)

  useEffect(() => {
    setDoingWork(sheetDoingWork)
    if (sheetDoingWork === false) {
      importObj.refresh()
    }
  }, [importObj, sheetDoingWork])

  return (
    <>
      {sheet.imported && (
        <div className="mt-5 rounded-md bg-green-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <Icon name="check-circle" className="h-5 w-5 text-green-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                Import successful
              </h3>
              <div className="mt-2 text-sm text-green-700">
                <p>
                  {sheet.imported_rows.length}{" "}
                  {pluralize("record", sheet.imported_rows.length)} imported by{" "}
                  {sheet.imported_by},{" "}
                  {formatAsRelativeDateTime(sheet.imported_at)}.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <ImportErrors errors={errors} />

      {doingWork ? (
        <div className="mt-4">
          <Loading inline delay={0} />
        </div>
      ) : (
        hasImportTableData && (
          <>
            <h3 className="mt-5 text-lg font-bold">Records</h3>
            <Table confirmationData={importTableData} />
            {canRevalidate && <RevalidateImport selectedSheet={sheet} />}
            {canConfirm && <ConfirmImport selectedSheet={sheet} />}
          </>
        )
      )}
    </>
  )
}
